import { useEntriesV2 } from '../../queries/use-entries';
import { Spinner } from '@cimplico/components';
import { Worksheet } from '../../types/worksheet';
import { useEffect, useMemo } from 'react';
import { useWorkpaperContext } from '../../providers/WorkpaperProvider';

type BulkCarryForwardResourcesRowProps = {
    worksheet: Worksheet;
    priorWorkpaperId: string;
    onGetEntries: (worksheetId: string, entryIds: string[]) => void;
};

function BulkCarryForwardResourcesRow({ worksheet, priorWorkpaperId, onGetEntries }: BulkCarryForwardResourcesRowProps): JSX.Element {
    const { id: worksheetId, name: worksheetName, priorWorksheetId } = worksheet;
    const { id: currentWorkpaperId } = useWorkpaperContext();

    const priorEntriesQuery = useEntriesV2(priorWorksheetId!, { sort: '' }, priorWorkpaperId);
    const currentEntriesQuery = useEntriesV2(worksheetId!, { sort: '' }, currentWorkpaperId);

    const isLoading = priorEntriesQuery.isLoading || currentEntriesQuery.isLoading;
    const isError = priorEntriesQuery.isError || currentEntriesQuery.isError;

    const priorEntries = priorEntriesQuery.data?.data ?? [];
    const currentEntries = currentEntriesQuery.data?.data ?? [];

    // Calculates eligible entries for carry forward by filtering out entries that already exist
    // in the current worksheet or have been previously carried forward
    const eligibleEntryIds = useMemo(() => {
        const exclude = currentEntries.reduce<Set<string>>((set, { id, priorEntryId }) => {
            set.add(id);
            if (priorEntryId) set.add(priorEntryId);
            return set;
        }, new Set());

        return priorEntries.filter((entry) => !entry.source && !exclude.has(entry.id)).map((entry) => entry.id);
    }, [priorEntries, currentEntries]);

    const isSuccess = priorEntriesQuery.isSuccess && currentEntriesQuery.isSuccess;

    useEffect(() => {
        if (isSuccess) {
            onGetEntries(worksheetId, eligibleEntryIds);
        }
    }, [worksheetId, eligibleEntryIds, onGetEntries, isSuccess]);

    return (
        <div className="col-span-full grid grid-cols-2 gap-x-6 border-b last:border-0 items-center py-2 px-4">
            <div className={`text-sm justify-self-start`}>
                <p className={`${isError ? 'text-red-600' : ''}`}>{worksheetName}</p>
            </div>
            <div className={`text-sm justify-self-start`}>
                {isLoading ? (
                    <Spinner size={'xs'} />
                ) : isError ? (
                    <p className={'text-red-600'}>Failed to retrieve entries.</p>
                ) : (
                    `${eligibleEntryIds.length}`
                )}
            </div>
        </div>
    );
}

export default BulkCarryForwardResourcesRow;
